import React from 'react';
import { ShoppingCart, Clock, User, Trash2, RotateCcw, Package } from 'lucide-react';
import { HeldCart } from '../../types/pos';
import { Button } from '../ui/Button';

interface HeldCartsPanelProps {
  heldCarts: HeldCart[];
  onRestore: (cart: HeldCart) => void;
  onDelete: (cartId: string) => void;
  loading?: boolean;
}

export const HeldCartsPanel: React.FC<HeldCartsPanelProps> = ({
  heldCarts,
  onRestore,
  onDelete,
  loading = false
}) => {
  const formatTimeRemaining = (expiresAt: string) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const diffMs = expires.getTime() - now.getTime();

    if (diffMs <= 0) return 'Expired';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatCreatedTime = (createdAt: string) => {
    const date = new Date(createdAt);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (heldCarts.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400">
        <ShoppingCart className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p className="text-sm font-medium">No held carts</p>
        <p className="text-xs mt-1">Hold a cart to save it for later</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-1 mb-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Held Carts ({heldCarts.length})
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Auto-expires in 24h
        </span>
      </div>

      {heldCarts.map(cart => (
        <div
          key={cart.id}
          className="p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20"
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-amber-100 dark:bg-amber-800/50 rounded-lg">
                <ShoppingCart className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                {cart.customer_name ? (
                  <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 text-gray-400" />
                    <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                      {cart.customer_name}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                    No customer
                  </span>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Package className="h-3 w-3" />
                    {cart.cart_items.length} item{cart.cart_items.length !== 1 ? 's' : ''}
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {formatCreatedTime(cart.created_at)}
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-bold text-amber-600 dark:text-amber-400">
                {'\u20AC'}{cart.total_amount.toFixed(2)}
              </div>
              <div className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                <Clock className="h-3 w-3" />
                {formatTimeRemaining(cart.expires_at)}
              </div>
            </div>
          </div>

          {cart.notes && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 italic bg-white dark:bg-gray-800 p-2 rounded">
              "{cart.notes}"
            </p>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => onRestore(cart)}
              variant="primary"
              size="sm"
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              disabled={loading}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Restore
            </Button>
            <Button
              onClick={() => onDelete(cart.id)}
              variant="secondary"
              size="sm"
              disabled={loading}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
